import { forwardRef, type InputHTMLAttributes, type SelectHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { styles } from '@/styles';

/*
  Fields are a label, a control, and — when something is wrong — one line
  saying what. The error line replaces the hint rather than stacking under it,
  because two lines of grey text under a red border is where people stop
  reading.
*/

interface FieldShellProps {
  label?: string;
  htmlFor?: string;
  /** Marks the control required and appends the asterisk to the label. */
  required?: boolean;
  hint?: string;
  error?: string;
  className?: string;
  children: ReactNode;
}

export function Field({ label, htmlFor, required, hint, error, className, children }: FieldShellProps) {
  return (
    <div className={cn('flex flex-col gap-1', className)}>
      {label && (
        <label htmlFor={htmlFor} className={styles.label}>
          {label}
          {required && <span className="ml-0.5 text-destructive">*</span>}
        </label>
      )}
      {children}
      {error ? (
        <p className="text-[10.5px] font-medium text-[hsl(var(--record-ink-danger))]">{error}</p>
      ) : hint ? (
        <p className="text-[10.5px] text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, invalid, ...rest },
  ref,
) {
  return (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(
        styles.input,
        invalid && 'border-destructive focus:border-destructive focus:ring-destructive/40',
        className,
      )}
      {...rest}
    />
  );
});

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { className, invalid, children, ...rest },
  ref,
) {
  return (
    <select
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(
        styles.select,
        invalid && 'border-destructive focus:border-destructive focus:ring-destructive/40',
        className,
      )}
      {...rest}
    >
      {children}
    </select>
  );
});
